import { type Dispatch, type FormEvent, type SetStateAction } from 'react';

interface HeroSectionProps {
  query: string;
  onQueryChange: Dispatch<SetStateAction<string>>;
  onTagShortcut: (tag: string) => void;
}

const STATS = [
  { id: 's-1', label: '在更条线', value: '386' },
  { id: 's-2', label: '避雷条目', value: '1,264' },
  { id: 's-3', label: '今夜新增', value: '6' },
  { id: 's-4', label: '已出结果', value: '312' },
];

const HOT_SHORTCUTS = ['塌房', '翻车', '争议', '丑闻', '已实锤', '出现反转'];

export default function HeroSection({ query, onQueryChange, onTagShortcut }: HeroSectionProps) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    document.getElementById('brief')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="overview"
      className="relative w-full overflow-hidden bg-foreground-950 text-background-50"
    >
      <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-primary-500/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-1/3 h-64 w-64 rounded-full bg-accent-500/15 blur-3xl" />

      <div className="relative grid w-full grid-cols-1 lg:grid-cols-12">
        <div className="flex flex-col justify-center px-4 py-14 md:px-8 md:py-20 lg:col-span-7 lg:py-24">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-background-50/20 bg-background-50/5 px-3.5 py-1.5 font-label text-[11px] tracking-[0.2em] text-accent-300">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
            深夜档案 · 每日 00:00 更新
          </span>

          <h1 className="mt-6 font-heading text-4xl font-black leading-tight tracking-wide text-background-50 md:text-6xl">
            深夜黑料
            <span className="mt-2 block h-1.5 w-20 rounded-full bg-primary-500" />
          </h1>

          <p className="mt-6 max-w-xl text-sm leading-relaxed text-background-50/75 md:text-base">
            专注做深夜黑料负面事件，把塌房、翻车、争议、丑闻按条线列成避雷，
            包含关键时间与最终结果。让你半夜知道不用每个帖子都翻，打开就能看清全貌。
          </p>

          <form onSubmit={handleSubmit} className="mt-8 w-full max-w-xl">
            <div className="flex flex-col gap-2 rounded-lg border border-background-50/15 bg-background-50/5 p-2 sm:flex-row sm:items-center">
              <span className="hidden h-10 w-10 shrink-0 items-center justify-center text-background-50/50 sm:flex">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="输入名字、关键词或条线编号，直接找避雷条目"
                className="h-11 w-full flex-1 rounded-md border border-transparent bg-transparent px-3 text-sm text-background-50 outline-none placeholder:text-background-50/40 focus:border-background-50/30"
              />
              <button
                type="submit"
                className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <i className="ri-filter-3-line text-base" />
                检索
              </button>
            </div>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-label text-[11px] tracking-[0.14em] text-background-50/45">
              常见入口
            </span>
            {HOT_SHORTCUTS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onTagShortcut(tag)}
                className="rounded-full border border-background-50/15 bg-background-50/5 px-3 py-1 text-xs text-background-50/80 transition-colors hover:border-accent-400/60 hover:text-accent-300"
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="mt-10 grid w-full grid-cols-2 gap-x-6 gap-y-5 border-t border-background-50/10 pt-7 sm:grid-cols-4">
            {STATS.map((stat) => (
              <div key={stat.id}>
                <p className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-background-50/55">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative min-h-[320px] lg:col-span-5 lg:min-h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Moody%20abstract%20night%20scene%20with%20deep%20crimson%20and%20amber%20light%20streaks%20across%20dark%20charcoal%20backdrop%2C%20smoke%20wisps%20and%20blurred%20distant%20city%20bokeh%2C%20cinematic%20film%20noir%20atmosphere%2C%20high%20contrast%20dark%20tones%20with%20warm%20amber%20glow%2C%20textured%20grain%2C%20no%20people&width=1000&height=1300&seq=snhe-hero-cover&orientation=portrait"
            alt="深夜黑料 塌房 翻车 争议 丑闻 话题封面"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/35 to-transparent lg:bg-gradient-to-r" />

          <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-3 lg:left-8">
            <div className="rounded-lg border border-background-50/15 bg-foreground-950/70 px-4 py-3 backdrop-blur">
              <p className="font-label text-[10px] tracking-[0.2em] text-accent-300">本夜新增</p>
              <p className="mt-1 font-heading text-xl font-bold text-background-50">6 条避雷</p>
            </div>
            <div className="rounded-lg border border-background-50/15 bg-foreground-950/70 px-4 py-3 backdrop-blur">
              <p className="font-label text-[10px] tracking-[0.2em] text-accent-300">最近更新</p>
              <p className="mt-1 font-heading text-xl font-bold text-background-50">00:42</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}